import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { hc, identifyUser } from "@/lib/heycatch";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Handles Emergent Auth redirect. Reads session_id from URL fragment,
 * exchanges it for our session cookie via backend, then routes to /dashboard.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    (async () => {
      if (!sessionId) {
        navigate("/", { replace: true });
        return;
      }
      try {
        // Attach any pending affiliate code captured before OAuth redirect.
        const ref = (typeof window !== "undefined" && window.sessionStorage.getItem("umbra_ref")) || null;
        const { data: sess } = await api.post("/auth/session", { session_id: sessionId, ref });
        if (typeof window !== "undefined") window.sessionStorage.removeItem("umbra_ref");
        // Clear the fragment so the token isn't leaked in history
        window.history.replaceState(null, "", window.location.pathname);
        const user = await refresh();
        if (user && sess?.is_new_user) {
          identifyUser(user);
          hc("trackEvent", "signup_completed");
        }
        if (user) {
          // Fire A/B `signup` event if this login originated from a paywall.
          try {
            const variant = localStorage.getItem("umbra_paywall_variant");
            const sid = localStorage.getItem("umbra_paywall_session");
            const returnTarget = window.sessionStorage.getItem("umbra_post_return");
            if (variant && sid) {
              const postSlug = returnTarget && returnTarget.startsWith("/blog/")
                ? returnTarget.slice("/blog/".length)
                : null;
              api.post("/blog/experiments/event", {
                experiment: "paywall_length",
                variant,
                event: "signup",
                session_id: sid,
                post_slug: postSlug,
              }).catch(() => {});
            }
          } catch { /* analytics never blocks the redirect */ }
          // Post-Return polish: if the user came from a blog post, send
          // them straight back there — no Dashboard flash.
          try {
            const target = window.sessionStorage.getItem("umbra_post_return");
            if (target && target.startsWith("/blog/")) {
              window.sessionStorage.removeItem("umbra_post_return");
              navigate(target, { replace: true });
              return;
            }
          } catch { /* sessionStorage disabled */ }
          navigate("/dashboard", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } catch (e) {
        console.error(e);
        toast.error("Sign-in did not complete. Please try again.");
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="font-sans uppercase tracking-[0.28em] text-xs text-bone-400" data-testid="auth-callback-loading">
        Opening the workbook…
      </p>
    </div>
  );
}
